import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toastError, toastSuccess } from "@/lib/toast";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import { startOrgSsoHop, ssoErrorMessage } from "@/lib/orgSso";
import { useConfirm } from "@/components/ConfirmDialogProvider";

function normalizeIssuerUrl(raw) {
  return String(raw || "")
    .trim()
    .replace(/\/+$/, "");
}

/**
 * Settings → Security. Two IdP apps per org: SSO hop (App 1) + directory import (App 2).
 */
const OrgSsoSettingsPanel = ({ orgId, accessToken }) => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entitled, setEntitled] = useState(null);
  const [form, setForm] = useState({
    issuer: "",
    client_id: "",
    client_secret: "",
    has_secret: false,
    directory_client_id: "",
    directory_client_secret: "",
    has_directory_secret: false,
    enabled: false,
    redirect_uri: "",
  });
  const [loadedIssuer, setLoadedIssuer] = useState("");
  const [bindingCount, setBindingCount] = useState(0);
  const [clearBindingsOnDisable, setClearBindingsOnDisable] = useState(false);
  const [clearingBindings, setClearingBindings] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [recovery, setRecovery] = useState({
    remaining: 0,
    warn_low: false,
    has_codes: false,
    canMint: false,
  });
  const [revealedCodes, setRevealedCodes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [minting, setMinting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dirTest, setDirTest] = useState({
    loading: false,
    provider: "",
    status: "idle", // idle | not_configured | ready | failed
    reason: "",
  });
  const [ssoTest, setSsoTest] = useState({
    status: "idle", // idle | pending | success | failed | skipped
    reason: "",
  });

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const applyConfig = (c) => {
    const issuer = c.issuer || "";
    setForm({
      issuer,
      client_id: c.client_id || "",
      client_secret: "",
      has_secret: Boolean(c.has_secret),
      directory_client_id: c.directory_client_id || "",
      directory_client_secret: "",
      has_directory_secret: Boolean(c.has_directory_secret),
      enabled: Boolean(c.enabled),
      redirect_uri: c.redirect_uri || "",
    });
    setLoadedIssuer(normalizeIssuerUrl(issuer));
    if (c.binding_count != null) {
      setBindingCount(Number(c.binding_count) || 0);
    }
    setRecovery((r) => ({
      ...r,
      remaining: Number(c.recovery_remaining) || 0,
      warn_low: Boolean(c.recovery_warn_low),
      has_codes: Boolean(c.recovery_has_codes),
    }));
  };

  const load = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    try {
      const statusRes = await axios.get(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/status`,
        { headers: authHeaders() }
      );
      const entitledNow = Boolean(statusRes.data?.entitled);
      setEntitled(entitledNow);
      if (!entitledNow) {
        return;
      }
      const cfgRes = await axios.get(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/config`,
        { headers: authHeaders() }
      );
      applyConfig(cfgRes.data || {});
      try {
        const bindRes = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/bindings`,
          { headers: authHeaders() }
        );
        setBindingCount(
          Number(bindRes.data?.binding_count ?? bindRes.data?.bindings?.length) ||
            0
        );
      } catch {
        /* manage_settings only; ignore */
      }
      try {
        const recRes = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/recovery-codes`,
          { headers: authHeaders() }
        );
        const rec = recRes.data || {};
        setRecovery({
          remaining: Number(rec.remaining) || 0,
          warn_low: Boolean(rec.warn_low),
          has_codes: Boolean(rec.has_codes),
          canMint: true,
        });
      } catch {
        setRecovery((r) => ({ ...r, canMint: false }));
      }
    } catch (err) {
      console.error(err);
      toastEntitlementOrError(err, "Could not load SSO settings");
      setEntitled(false);
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(
        `org-sso-dir-test:${encodeURIComponent(orgId || "")}`,
      );
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setDirTest({
        loading: false,
        provider: String(parsed?.provider || ""),
        status: String(parsed?.status || "idle"),
        reason: String(parsed?.reason || ""),
      });
    } catch {
      // ignore persisted-state parse failures
    }
  }, [orgId]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        `org-sso-dir-test:${encodeURIComponent(orgId || "")}`,
        JSON.stringify({
          provider: dirTest.provider,
          status: dirTest.status,
          reason: dirTest.reason,
        }),
      );
    } catch {
      // ignore storage errors
    }
  }, [orgId, dirTest.provider, dirTest.status, dirTest.reason]);

  useEffect(() => {
    const err = searchParams.get("sso_error");
    const ok = searchParams.get("sso");
    if (!err && ok !== "ok") return;
    if (ok === "ok") {
      setSsoTest({
        status: "success",
        reason: "SSO test succeeded. You can enable it after recovery codes are saved.",
      });
    } else if (err) {
      setSsoTest({
        status: "failed",
        reason: ssoErrorMessage(err),
      });
    }
    const next = new URLSearchParams(searchParams);
    next.delete("sso_error");
    next.delete("sso");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const canTestHop =
    form.issuer.trim() &&
    form.client_id.trim() &&
    (form.has_secret || form.client_secret.trim());

  const canTestDirectory =
    form.issuer.trim() &&
    form.directory_client_id.trim() &&
    (form.has_directory_secret || form.directory_client_secret.trim());

  const save = async ({ enabled, clear_bindings } = {}) => {
    setSaving(true);
    try {
      const body = {
        issuer: form.issuer.trim(),
        client_id: form.client_id.trim(),
        directory_client_id: form.directory_client_id.trim(),
      };
      if (form.client_secret.trim()) {
        body.client_secret = form.client_secret.trim();
      }
      if (form.directory_client_secret.trim()) {
        body.directory_client_secret = form.directory_client_secret.trim();
      }
      if (typeof enabled === "boolean") {
        body.enabled = enabled;
      }
      if (clear_bindings === true) {
        body.clear_bindings = true;
      }
      const res = await axios.put(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/config`,
        body,
        { headers: authHeaders() }
      );
      applyConfig(res.data || {});
      if (clear_bindings === true) {
        setBindingCount(Number(res.data?.binding_count) || 0);
      }
      toastSuccess(
        enabled === true
          ? "SSO enabled for this workplace"
          : enabled === false
            ? "SSO disabled"
            : "SSO settings saved"
      );
      return true;
    } catch (err) {
      toastEntitlementOrError(err, "Could not save SSO settings");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = async () => {
    const nextIssuer = normalizeIssuerUrl(form.issuer);
    const issuerChanged =
      loadedIssuer && nextIssuer && loadedIssuer !== nextIssuer;
    if (issuerChanged && bindingCount > 0) {
      const ok = await confirm({
        title: "Switch identity provider?",
        description:
          "Changing the issuer clears all existing SSO links. Everyone must hop through the new IdP again. This cannot be undone.",
        confirmLabel: "Clear links and save",
        variant: "destructive",
      });
      if (!ok) return;
      await save({ clear_bindings: true });
      return;
    }
    await save();
  };

  const handleEnable = async (next) => {
    if (next) {
      const ok = await confirm({
        title: "Enable SSO for this workplace?",
        description:
          "Everyone, including the owner and org admins, will hop through this identity provider when entering. Keep recovery codes somewhere safe. Test SSO first if you have not already.",
        confirmLabel: "Enable SSO",
      });
      if (!ok) return;
      await save({ enabled: true });
      return;
    }
    const ok = await confirm({
      title: "Disable SSO?",
      description: clearBindingsOnDisable
        ? "SSO will turn off and all IdP links will be cleared. Members will need to hop again if you re-enable."
        : "SSO will turn off. Existing IdP links are kept unless you checked “Also clear all IdP links”.",
      confirmLabel: clearBindingsOnDisable
        ? "Disable and clear links"
        : "Disable SSO",
      variant: "destructive",
    });
    if (!ok) return;
    await save({
      enabled: false,
      clear_bindings: clearBindingsOnDisable ? true : undefined,
    });
    if (clearBindingsOnDisable) setClearBindingsOnDisable(false);
  };

  const handleClearAllBindings = async () => {
    const ok = await confirm({
      title: "Unlink everyone?",
      description:
        "Removes all IdP links for this workplace. Members must complete SSO hop again on next enter (if SSO is enabled).",
      confirmLabel: "Unlink everyone",
      variant: "destructive",
    });
    if (!ok) return;
    setClearingBindings(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/bindings/clear`,
        {},
        { headers: authHeaders() }
      );
      setBindingCount(0);
      toastSuccess(
        `Cleared ${Number(res.data?.cleared) || 0} SSO link${
          Number(res.data?.cleared) === 1 ? "" : "s"
        }`
      );
    } catch (err) {
      toastEntitlementOrError(err, "Could not clear SSO links");
    } finally {
      setClearingBindings(false);
    }
  };

  const handleResetSso = async () => {
    const ok = await confirm({
      title: "Reset SSO configuration?",
      description:
        "Wipes IdP credentials (App 1 and App 2), clears all SSO links, disables SSO, and generates a new recovery code set (shown once). This cannot be undone.",
      confirmLabel: "Reset SSO",
      variant: "destructive",
    });
    if (!ok) return;
    setResetting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/reset`,
        {},
        { headers: authHeaders() }
      );
      const data = res.data || {};
      applyConfig(data);
      setBindingCount(Number(data.binding_count) || 0);
      setRevealedCodes(Array.isArray(data.codes) ? data.codes : []);
      setRecovery({
        remaining: Number(data.recovery_remaining ?? data.remaining) || 0,
        warn_low: Boolean(data.recovery_warn_low ?? data.warn_low),
        has_codes: Boolean(data.recovery_has_codes ?? data.has_codes),
        canMint: true,
      });
      toastSuccess("SSO reset. Save the new recovery codes now.");
    } catch (err) {
      toastEntitlementOrError(err, "Could not reset SSO");
    } finally {
      setResetting(false);
    }
  };

  const handleMint = async () => {
    if (recovery.has_codes) {
      const ok = await confirm({
        title: "Generate a new recovery set?",
        description:
          "This voids unused codes from the previous set. The new codes are shown once — copy or download them now.",
        confirmLabel: "Generate new codes",
      });
      if (!ok) return;
    }
    setMinting(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/recovery-codes`,
        {},
        { headers: authHeaders() }
      );
      const rec = res.data || {};
      setRevealedCodes(Array.isArray(rec.codes) ? rec.codes : []);
      setRecovery({
        remaining: Number(rec.remaining) || 0,
        warn_low: Boolean(rec.warn_low),
        has_codes: Boolean(rec.has_codes),
        canMint: true,
      });
      toastSuccess("Recovery codes generated. Save them now — they will not be shown again.");
    } catch (err) {
      toastEntitlementOrError(err, "Could not generate recovery codes");
    } finally {
      setMinting(false);
    }
  };

  const copyCodes = async () => {
    if (!revealedCodes?.length) return;
    try {
      await navigator.clipboard.writeText(revealedCodes.join("\n"));
      toastSuccess("Recovery codes copied");
    } catch {
      toastError("Could not copy recovery codes");
    }
  };

  const downloadCodes = () => {
    if (!revealedCodes?.length) return;
    const blob = new Blob([revealedCodes.join("\n") + "\n"], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `circa-sso-recovery-${orgId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTest = async () => {
    const ok = await confirm({
      title: "Test SSO + directory import?",
      description:
        "This hop binds your Circa account to the IdP person you sign in as (iss + sub). A member cannot later hop as that same IdP user. Use a dedicated admin IdP account if you will also invite yourself as a member.",
      confirmLabel: "Run test",
    });
    if (!ok) return;
    setTesting(true);
    setSsoTest({ status: "pending", reason: "Checking directory import…" });
    try {
      if (
        form.issuer.trim() ||
        form.client_id.trim() ||
        form.client_secret.trim() ||
        form.directory_client_id.trim() ||
        form.directory_client_secret.trim()
      ) {
        await save();
      }
      setDirTest((s) => ({ ...s, loading: true, reason: "" }));
      if (!canTestDirectory) {
        setDirTest({
          loading: false,
          provider: "",
          status: "not_configured",
          reason:
            "Add directory client ID and secret (App 2) below, then run this test again.",
        });
      } else {
        const capRes = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/directory/capability`,
          { headers: authHeaders() }
        );
        const cap = capRes.data || {};
        const ready = Boolean(cap.directory_ready);
        setDirTest({
          loading: false,
          provider: String(cap.provider || ""),
          status: ready ? "ready" : "failed",
          reason: ready ? "" : String(cap.reason || "Directory import is not ready."),
        });
      }
      if (!canTestHop) {
        setSsoTest({
          status: "skipped",
          reason: "SSO hop not started — complete App 1 credentials first.",
        });
        setTesting(false);
        return;
      }
      setSsoTest({
        status: "pending",
        reason: "Redirecting to IdP for SSO test…",
      });
      await startOrgSsoHop(orgId, {
        mode: "test",
        returnPath: `/orgs/${orgId}/settings?section=security`,
      });
    } catch (err) {
      toastError(err, "Could not start SSO test");
      setDirTest((s) => ({ ...s, loading: false }));
      setSsoTest({
        status: "failed",
        reason: "Could not start SSO test.",
      });
      setTesting(false);
    }
  };

  const copyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(form.redirect_uri);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toastError("Could not copy redirect URI");
    }
  };

  if (loading || entitled == null) {
    return <p className="text-sm text-muted-foreground">Loading SSO settings…</p>;
  }

  if (!entitled) {
    return (
      <div className="max-w-3xl space-y-6">
        <div>
          <h3 className="text-base font-semibold">Security</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Connect an OpenID Connect identity provider so members step up when
            they enter this workplace. Circa login stays email and password.
          </p>
        </div>
        <div className="rounded-lg border border-border/80 bg-muted/30 px-4 py-4 text-sm text-muted-foreground space-y-3">
          <p>
            SSO requires{" "}
            <span className="font-medium text-foreground">Business</span> or
            higher.
          </p>
          <p>
            See{" "}
            <Link
              to="/about"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              About Circa
            </Link>{" "}
            or{" "}
            <Link
              to={`/orgs/${encodeURIComponent(orgId)}/settings?section=plan`}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Plan &amp; usage
            </Link>
            .
          </p>
        </div>
      </div>
    );
  }

  const canEnable = canTestHop && recovery.remaining > 0;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h3 className="text-base font-semibold">Security</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Circa login stays email and password. You need{" "}
          <span className="font-medium text-foreground">two IdP apps</span> in
          the same tenant: App 1 for workplace SSO hop, App 2 for directory
          import. Owner and admins hop too. Recovery codes are break-glass if
          the IdP is down.
        </p>
        <ol className="mt-3 list-decimal space-y-0.5 pl-5 text-xs text-muted-foreground">
          <li>Create App 1 (SSO hop) and App 2 (directory M2M) in your IdP</li>
          <li>Paste credentials for both apps below; Save</li>
          <li>Generate recovery codes and store them</li>
          <li>
            Test SSO + directory import (binds{" "}
            <span className="font-medium text-foreground">your</span> Circa user
            to that IdP person — do not reuse that IdP user for a member)
          </li>
          <li>Enable SSO</li>
        </ol>
      </div>

      <div className="space-y-4 rounded-lg border border-border/80 p-4">
        <div>
          <p className="text-sm font-medium">App 1 — Workplace SSO hop</p>
          <p className="text-xs text-muted-foreground">
            Confidential OpenID Connect web app. Circa starts the hop when
            people enter this workplace.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sso-redirect">Redirect URI (Sign-in redirect)</Label>
          <div className="flex gap-2">
            <Input
              id="sso-redirect"
              value={form.redirect_uri}
              readOnly
              className="font-mono text-xs"
            />
            <Button type="button" variant="outline" onClick={copyRedirect}>
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Must match exactly. The same URI is used for every Circa org;{" "}
            <code className="font-mono text-[0.7rem]">state</code> selects the
            org.
          </p>
        </div>
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
          <li>
            Confidential <span className="font-medium text-foreground">web</span>{" "}
            app with a{" "}
            <span className="font-medium text-foreground">client secret</span>
          </li>
          <li>
            Grant:{" "}
            <span className="font-medium text-foreground">Authorization code</span>
          </li>
          <li>
            <span className="font-medium text-foreground">PKCE S256</span>
          </li>
          <li>
            Scopes:{" "}
            <span className="font-medium text-foreground">openid</span>{" "}
            (<code className="font-mono text-[0.7rem]">profile</code> /{" "}
            <code className="font-mono text-[0.7rem]">email</code> optional)
          </li>
        </ul>
        <div className="space-y-1.5">
          <Label htmlFor="sso-issuer">Issuer URL</Label>
          <Input
            id="sso-issuer"
            value={form.issuer}
            onChange={(e) => setForm((f) => ({ ...f, issuer: e.target.value }))}
            placeholder="https://dev-xxxxx.okta.com"
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">
            Same issuer for both apps. Entra example:{" "}
            <code className="font-mono text-[0.7rem]">
              https://login.microsoftonline.com/{"{tenant-id}"}/v2.0
            </code>
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sso-client">Client ID</Label>
          <Input
            id="sso-client"
            value={form.client_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, client_id: e.target.value }))
            }
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sso-secret">Client secret</Label>
          <Input
            id="sso-secret"
            type="password"
            value={form.client_secret}
            onChange={(e) =>
              setForm((f) => ({ ...f, client_secret: e.target.value }))
            }
            placeholder={form.has_secret ? "Saved — leave blank to keep" : ""}
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-border/80 p-4">
        <div>
          <p className="text-sm font-medium">App 2 — Directory import</p>
          <p className="text-xs text-muted-foreground">
            Separate machine-to-machine client in the{" "}
            <span className="font-medium text-foreground">same tenant</span>.
            Used only to list users and groups for invites — not for workplace
            hop.
          </p>
        </div>
        <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          <li>
            <span className="font-medium text-foreground">Okta:</span> grant
            service app scopes{" "}
            <code className="font-mono text-[0.7rem]">okta.users.read</code> and{" "}
            <code className="font-mono text-[0.7rem]">okta.groups.read</code>.
          </li>
          <li>
            <span className="font-medium text-foreground">Microsoft Entra:</span>{" "}
            add Microsoft Graph application permissions{" "}
            <code className="font-mono text-[0.7rem]">User.Read.All</code> and{" "}
            <code className="font-mono text-[0.7rem]">Group.Read.All</code>, then
            grant admin consent.
          </li>
          <li>
            <span className="font-medium text-foreground">Keycloak:</span> service
            account with{" "}
            <code className="font-mono text-[0.7rem]">realm-management</code>{" "}
            view/query users and groups.
          </li>
        </ul>
        <div className="space-y-1.5">
          <Label htmlFor="dir-client">Directory client ID</Label>
          <Input
            id="dir-client"
            value={form.directory_client_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, directory_client_id: e.target.value }))
            }
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dir-secret">Directory client secret</Label>
          <Input
            id="dir-secret"
            type="password"
            value={form.directory_client_secret}
            onChange={(e) =>
              setForm((f) => ({ ...f, directory_client_secret: e.target.value }))
            }
            placeholder={
              form.has_directory_secret ? "Saved — leave blank to keep" : ""
            }
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border/80 p-4">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={handleSaveClick}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={(!canTestHop && !canTestDirectory) || testing || saving}
            onClick={handleTest}
          >
            {testing || dirTest.loading
              ? "Testing…"
              : "Test SSO + directory import"}
          </Button>
        </div>
        <div className="space-y-1 text-xs">
          {dirTest.status === "not_configured" ? (
            <p className="text-muted-foreground">
              Directory import: not configured — {dirTest.reason}
            </p>
          ) : null}
          {dirTest.status === "failed" ? (
            <p className="text-destructive">
              Directory import failed
              {dirTest.provider ? ` (${dirTest.provider})` : ""}: {dirTest.reason}
            </p>
          ) : null}
          {dirTest.status === "ready" ? (
            <p className="text-emerald-600">
              Directory import: ready
              {dirTest.provider ? ` (${dirTest.provider})` : ""}.
            </p>
          ) : null}
          {ssoTest.status === "pending" ? (
            <p className="text-muted-foreground">SSO hop: {ssoTest.reason}</p>
          ) : null}
          {ssoTest.status === "success" ? (
            <p className="text-emerald-600">SSO hop: {ssoTest.reason}</p>
          ) : null}
          {ssoTest.status === "failed" ? (
            <p className="text-destructive">SSO hop failed: {ssoTest.reason}</p>
          ) : null}
          {ssoTest.status === "skipped" ? (
            <p className="text-amber-600">{ssoTest.reason}</p>
          ) : null}
        </div>
      </div>

      {recovery.canMint ? (
        <div className="space-y-3 rounded-lg border border-border/80 p-4">
          <div>
            <p className="text-sm font-medium">Recovery codes</p>
            <p className="text-xs text-muted-foreground">
              Org-level one-time codes. Shown only when generated. Owner and
              admins need a Circa password plus one code if the IdP is down.
            </p>
          </div>
          {recovery.warn_low ? (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
              {recovery.remaining} recovery{" "}
              {recovery.remaining === 1 ? "code" : "codes"} remaining. Generate
              a new set soon.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {recovery.has_codes
                ? `${recovery.remaining} unused code${
                    recovery.remaining === 1 ? "" : "s"
                  } remaining.`
                : form.enabled
                  ? "No unused codes left. Generate a new set while you still have a workplace session."
                  : "No recovery codes yet. Generate a set before enabling SSO."}
            </p>
          )}
          {revealedCodes?.length ? (
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
              <p className="text-xs font-medium">
                Save these now. They will not be shown again.
              </p>
              <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
                {revealedCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={copyCodes}>
                  Copy
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadCodes}
                >
                  Download
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setRevealedCodes(null)}
                >
                  I saved these
                </Button>
              </div>
            </div>
          ) : null}
          <Button
            type="button"
            variant="outline"
            disabled={minting}
            onClick={handleMint}
          >
            {minting
              ? "Generating…"
              : recovery.has_codes
                ? "Generate new set"
                : "Generate recovery codes"}
          </Button>
        </div>
      ) : null}

      <div className="space-y-3 rounded-lg border border-border/80 p-4">
        <div>
          <p className="text-sm font-medium">SSO links</p>
          <p className="text-xs text-muted-foreground">
            {bindingCount === 0
              ? "No members are linked to an IdP identity yet."
              : `${bindingCount} member${
                  bindingCount === 1 ? "" : "s"
                } linked to an IdP identity.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={clearingBindings || bindingCount < 1 || saving}
            onClick={handleClearAllBindings}
          >
            {clearingBindings ? "Unlinking…" : "Unlink everyone"}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={resetting || saving}
            onClick={handleResetSso}
          >
            {resetting ? "Resetting…" : "Reset SSO"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Reset wipes IdP credentials, clears links, disables SSO, and mints new
          recovery codes.
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-border/80 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Enable SSO</p>
            <p className="text-xs text-muted-foreground">
              {recovery.remaining > 0
                ? "Owner, admins, and members must complete this hop to enter."
                : "Generate recovery codes before enabling."}
            </p>
          </div>
          <Switch
            checked={form.enabled}
            disabled={saving || (!form.enabled && !canEnable)}
            onCheckedChange={handleEnable}
          />
        </div>
        {form.enabled ? (
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={clearBindingsOnDisable}
              onChange={(e) => setClearBindingsOnDisable(e.target.checked)}
            />
            <span>
              When disabling, also clear all IdP links (everyone must hop again
              after re-enable).
            </span>
          </label>
        ) : null}
      </div>
    </div>
  );
};

export default OrgSsoSettingsPanel;
